// ConfirmDialog — the app's confirmation prompt.
//
// WHY THIS EXISTS. 41 files call window.confirm. That has three problems, and testers hit
// all of them this week:
//
//  1. It is an OS dialog dropped into a themed app, so it reads as the page having fallen
//     out of the product.
//  2. A dismissed confirm returns false and the caller returns immediately, which is
//     indistinguishable from a dead button: no request, no toast, nothing. That is exactly
//     how "Delete Rate Card does nothing" and the Locations remove were both reported.
//  3. Automated drivers auto-dismiss native dialogs, so every destructive path guarded by
//     one is untestable by construction — which is why these survived several rounds.
//
// Behaviour matches what window.confirm gave for free, because a hand-rolled dialog
// usually forgets it: Escape cancels, the backdrop cancels, and focus moves into the
// dialog and returns to whatever opened it.
//
// FOCUS STARTS ON CANCEL, deliberately. For a destructive action the safe choice should be
// the one a stray Enter hits. Jobber, HouseCall Pro and ServiceTitan all do this.
//
// The backdrop handler fires on mouseDown against the target itself, so a drag that starts
// inside the dialog and ends outside is not treated as a dismiss.

import React, { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const TONE = {
  danger: {
    confirm: 'bg-red-600 hover:bg-red-700 text-white',
    icon: 'text-red-600 dark:text-red-400'
  },
  primary: {
    confirm: 'bg-blue-600 hover:bg-blue-700 text-white',
    icon: 'text-blue-600 dark:text-blue-400'
  }
};

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel
}) => {
  const cancelRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    cancelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Put focus back where it came from, or a keyboard user is dropped at the top of
      // the document every time they cancel something.
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, busy, onCancel]);

  if (!open) return null;
  const styles = TONE[tone] || TONE.danger;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={message ? 'confirm-dialog-message' : undefined}
        className="w-full max-w-md rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
      >
        <div className="flex gap-3">
          <ExclamationTriangleIcon className={`h-6 w-6 flex-shrink-0 ${styles.icon}`} aria-hidden="true" />
          <div className="min-w-0">
            <h3 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {message && (
              <p id="confirm-dialog-message" className="mt-2 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {message}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 text-sm rounded-md disabled:opacity-50 ${styles.confirm}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
